import { useWatch, Controller } from "react-hook-form";
import { useEffect, useRef } from "react";
import { Autocomplete, Checkbox, TextField } from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useFetchOLTs } from "@/shared/actions/erp/configuracion-red/olts.actions";
import { useFetchGetServidores } from "@/shared/actions";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
const SELECT_ALL = "__ALL__";

export function ServicioAfectadoRow({ index, control, errors, setValue }: any) {
  const cliente = useWatch({ control, name: `serviciosAfectados.${index}.clienteAfectado` });

  const { data: oltsResp, isLoading: loadingOlt } = useFetchOLTs({
    enabled: cliente === "OLT",
    params: { page: 1, page_size: 1000 },
  });
  const { data: servidoresResp, isLoading: loadingServ } = useFetchGetServidores(
    { page: 0, size: 1000 },
    cliente === "SERVIDORES"
  );

  // opciones combinando Nombre + IP para que sean strings únicos
  const opcionesServicio: string[] =
    cliente === "OLT"
      ? (oltsResp?.data?.data ?? []).map((o: any) => {
          const nombre = o.nombre ?? o.codigo;
          // Si la OLT tiene IP la ponemos, si no, dejamos solo el nombre
          return o.ip ? `${nombre} (${o.ip})` : nombre;
        })
      : cliente === "SERVIDORES"
        ? (servidoresResp?.data ?? [])
            .filter((s: any) => s.estado)
            .map((s: any) => {
              const ip = s.ipServidor || s.ip || "Sin IP";
              return `${s.nombreServidor} (${ip})`;
            })
        : [];

  // Al cambiar el cliente, limpia el servicio anterior
 const clienteAnterior = useRef<string>("");
  useEffect(() => {
    const curr = cliente ?? "";
    const prev = clienteAnterior.current;
    clienteAnterior.current = curr;
    // limpiar SOLO si antes ya había un cliente real y el usuario lo cambió a otro distinto
    if (prev && curr && prev !== curr) {
      setValue(`serviciosAfectados.${index}.servicio`, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cliente]);

  return (
    <Controller
      control={control}
      name={`serviciosAfectados.${index}.servicio`}
      render={({ field }) => {
        // CSV "A (10.0.0.1), B (10.0.0.2)" -> ["A (10.0.0.1)","B (10.0.0.2)"]
        const seleccionados: string[] = (field.value ?? "")
          .split(",")
          .map((s: string) => s.trim())
          .filter(Boolean);

        const todosSeleccionados =
          opcionesServicio.length > 0 &&
          seleccionados.length === opcionesServicio.length;

        return (
          <Autocomplete
            multiple
            limitTags={2} // Bajado a 2 para que no se desborde la fila por el largo de la IP
            disableCloseOnSelect
            options={[SELECT_ALL, ...opcionesServicio]}
            value={seleccionados}
            loading={loadingOlt || loadingServ}
            isOptionEqualToValue={(opt, val) => opt === val}
            getOptionLabel={(option) =>
              option === SELECT_ALL ? "Seleccionar todos" : option
            }
            onChange={(_e, data: string[]) => {
              if (data.includes(SELECT_ALL)) {
                const nuevos = todosSeleccionados ? [] : opcionesServicio;
                field.onChange(nuevos.join(", "));
                return;
              }
              field.onChange(data.join(", "));
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={option === SELECT_ALL ? todosSeleccionados : selected}
                />
                {option === SELECT_ALL ? "Seleccionar todos" : option}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Servicio"
                error={!!errors.serviciosAfectados?.[index]?.servicio}
                helperText={errors.serviciosAfectados?.[index]?.servicio?.message ?? ""}
              />
            )}
          />
        );
      }}
    />
  );
}